import type { ElementType } from 'react';
import type {
    AdditionalAttributeChangeProps,
    AttributeChangeInformationProps,
    AttributeChangeProps
} from '../types';
import DogClubChange from '../components/EditCoverableAttribute/pet/DogClubChange';
import { type EditCoverablePriceDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/opc';
import type { OPCIdentifier } from '../../../recoil/types/opcTypes';
import PetChipIdChange from '../components/EditCoverableAttribute/pet/PetChipIdChange';
import { type DSSPaymentFrequency } from 'gw-edge-client/dist/types/dto/pc/typekey';
import ContentVoluntaryAlarmChange from '../components/EditCoverableAttribute/privateContent/ContentVoluntaryAlarmChange';
import { getEditCoverableChangeProps } from './EditCoverableConfig';
import CarOdometerChange from '../components/EditCoverableAttribute/car/CarOdometerChange';
import TravelChangeFlavour from '../components/EditCoverableAttribute/travel/TravelChangeFlavour';
import ChangeAreaOfDestination from '../components/EditCoverableAttribute/travel/ChangeAreaOfDestination';
import AdultAccEmploymentChange from '../components/EditCoverableAttribute/accident/AdultAccEmploymentChange';
import AdultAccFulltimeLeisureChange from '../components/EditCoverableAttribute/accident/AdultAccFulltimeLeisureChange';
import CarYearlyMileageChange from '../components/EditCoverableAttribute/car/CarYearlyMileageChange';
import AdultAccContractTypeChange from '../components/EditCoverableAttribute/accident/AdultAccContractTypeChange';
import InsurancePeriodChange from '../components/EditCoverableAttribute/cho/InsurancePeriodChange';
import HouseToiletChange from '../components/EditCoverableAttribute/house/HouseToiletChange';
import HousePrimaryHeatingChange from '../components/EditCoverableAttribute/house/HousePrimaryHeatingChange';
import HolidayHomeFirePlaceChange from '../components/EditCoverableAttribute/house/HolidayHomeFirePlaceChange';

interface EditAttribute {
    title: string;
    AttributeChangeComponent: ElementType<AttributeChangeProps>;
    attributeChangeAditionalProps?: AdditionalAttributeChangeProps;
    attributeChangeInformationProps?: AttributeChangeInformationProps;
}

const getEditAttributeConfig = (
    opcInfo: OPCIdentifier,
    getHippoText: (resourceKey: string) => string,
    editCoverablePrice?: EditCoverablePriceDTO,
    selectedPaymentFrequency?: DSSPaymentFrequency
): EditAttribute => {
    const switchToIndividualTravelOrBackToBefore =
        editCoverablePrice?.travel?.find(
            (editedCoverableValue) =>
                editedCoverableValue.attribute === 'TravelType' &&
                String(editedCoverableValue.after?.typeKeyValue) === 'Individual' &&
                String(editedCoverableValue?.before?.typeKeyValue) === 'Household'
        ) ??
        editCoverablePrice?.travel?.find(
            (editedCoverableValue) =>
                editedCoverableValue.attribute === 'TravelType' &&
                String(editedCoverableValue.after?.typeKeyValue) ===
                    String(editedCoverableValue?.before?.typeKeyValue)
        );
    const sameAreaDestination = editCoverablePrice?.travel?.find(
        (editedCoverableValue) =>
            editedCoverableValue.attribute === 'AreaDestination' &&
            String(editedCoverableValue.after?.typeKeyValue) ===
                String(editedCoverableValue?.before?.typeKeyValue)
    );
    switch (opcInfo.attribute) {
        case 'dogClub':
            return {
                title: getHippoText('DSS-PET-OPC-EDIT-DOG-CLUB-TITLE'),
                AttributeChangeComponent: DogClubChange,
                ...(editCoverablePrice
                    ? {
                          attributeChangeInformationProps: getEditCoverableChangeProps(
                              editCoverablePrice,
                              opcInfo.productName,
                              selectedPaymentFrequency
                          )
                      }
                    : {})
            };
        case 'dogChipId':
        case 'catChipId':
            return {
                title: getHippoText('DSS-PET-OPC-EDIT-CHIP-NUMBER-TITLE'),
                AttributeChangeComponent: PetChipIdChange
            };
        case 'prcVoluntaryAlarm':
            return {
                title: 'Frivillig tyverialarm', // Todo: Use hippo label when label id is define in document
                AttributeChangeComponent: ContentVoluntaryAlarmChange,
                ...(editCoverablePrice
                    ? {
                          attributeChangeInformationProps: getEditCoverableChangeProps(
                              editCoverablePrice,
                              opcInfo.productName,
                              selectedPaymentFrequency
                          )
                      }
                    : {})
            };
        case 'carOdometer':
            return {
                title: 'Opdater kilometeraflæsning',
                AttributeChangeComponent: CarOdometerChange
            };
        case 'traNumberInHousehold':
            return {
                title: 'DSS-COVERABLE-INFORMATION-STEP-ADDRESS-HEADER',
                AttributeChangeComponent: TravelChangeFlavour,
                ...(editCoverablePrice && !switchToIndividualTravelOrBackToBefore
                    ? {
                          attributeChangeInformationProps: getEditCoverableChangeProps(
                              editCoverablePrice,
                              opcInfo.productName,
                              selectedPaymentFrequency
                          )
                      }
                    : {})
            };
        case 'traAreaOfDestination':
            return {
                title: 'DSS-COVERABLE-INFORMATION-STEP-AREA-OF-DESTINATION-HEADER',
                AttributeChangeComponent: ChangeAreaOfDestination,
                ...(editCoverablePrice && !sameAreaDestination
                    ? {
                          attributeChangeInformationProps: getEditCoverableChangeProps(
                              editCoverablePrice,
                              opcInfo.productName,
                              selectedPaymentFrequency
                          )
                      }
                    : {})
            };
        case 'adultAccEmployment':
            return {
                title: 'DSS-COVERABLE-DETAILS-STEP-EMPLOYMENT-HEADER',
                AttributeChangeComponent: AdultAccEmploymentChange,
                attributeChangeAditionalProps: {
                    isPriceIncrease:
                        Number(editCoverablePrice?.newPrice?.yearlyTotal.amount) >
                        Number(editCoverablePrice?.oldPrice?.yearlyTotal.amount)
                },
                ...(editCoverablePrice
                    ? {
                          attributeChangeInformationProps: getEditCoverableChangeProps(
                              editCoverablePrice,
                              opcInfo.productName,
                              selectedPaymentFrequency
                          )
                      }
                    : {})
            };
        case 'carYearlyMileage':
            return {
                title: 'Tilpas dit årlige kørselsbehov',
                AttributeChangeComponent: CarYearlyMileageChange,
                ...(editCoverablePrice
                    ? {
                          attributeChangeInformationProps: getEditCoverableChangeProps(
                              editCoverablePrice,
                              opcInfo.productName,
                              selectedPaymentFrequency
                          )
                      }
                    : {})
            };
        case 'adultAccFulltimeLeisure':
            return {
                title: 'DSS-COVERABLE-DETAILS-STEP-FULLTIME-OR-LEISURE-HEADER',
                AttributeChangeComponent: AdultAccFulltimeLeisureChange,
                ...(editCoverablePrice
                    ? {
                          attributeChangeInformationProps: getEditCoverableChangeProps(
                              editCoverablePrice,
                              opcInfo.productName,
                              selectedPaymentFrequency
                          )
                      }
                    : {})
            };
        case 'adultAccContractType':
            return {
                title: 'DSS-COVERABLE-DETAILS-STEP-CONTRACT-TYPE-HEADER',
                AttributeChangeComponent: AdultAccContractTypeChange,
                ...(editCoverablePrice
                    ? {
                          attributeChangeInformationProps: getEditCoverableChangeProps(
                              editCoverablePrice,
                              opcInfo.productName,
                              selectedPaymentFrequency
                          )
                      }
                    : {})
            };
        case 'choInsurancePeriod':
            return {
                title: 'DSS-COVERABLE-EXTEND-INSURANCEPERIOD-TITLE',
                AttributeChangeComponent: InsurancePeriodChange,
                ...(editCoverablePrice
                    ? {
                          attributeChangeInformationProps: getEditCoverableChangeProps(
                              editCoverablePrice,
                              opcInfo.productName,
                              selectedPaymentFrequency
                          )
                      }
                    : {})
            };
        case 'prhNumOfToilets':
            return {
                title: 'DSS-EDIT-NUMBER-OF-TOILETS-TITLE',
                AttributeChangeComponent: HouseToiletChange,
                ...(editCoverablePrice
                    ? {
                          attributeChangeInformationProps: getEditCoverableChangeProps(
                              editCoverablePrice,
                              opcInfo.productName,
                              selectedPaymentFrequency
                          )
                      }
                    : {})
            };
        case 'prhPrimaryHeating':
            return {
                title: 'DSS-EDIT-PRIMARY-HEATING-TITLE',
                AttributeChangeComponent: HousePrimaryHeatingChange,
                ...(editCoverablePrice
                    ? {
                          attributeChangeInformationProps: getEditCoverableChangeProps(
                              editCoverablePrice,
                              opcInfo.productName,
                              selectedPaymentFrequency
                          )
                      }
                    : {})
            };
        case 'prhFirePlace':
            return {
                title: 'DSS-EDIT-WOOD-STOVE-FIREPLACE-QUESTION',
                AttributeChangeComponent: HolidayHomeFirePlaceChange,
                ...(editCoverablePrice
                    ? {
                          attributeChangeInformationProps: getEditCoverableChangeProps(
                              editCoverablePrice,
                              opcInfo.productName,
                              selectedPaymentFrequency
                          )
                      }
                    : {})
            };
        case '':
            throw new Error('No attribute was found');
        default:
            throw new Error(`Unsupported for attribute: ${String(opcInfo.attribute)}`);
    }
};

export default getEditAttributeConfig;
